import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ContactModal from '../components/ContactModal';
import { Button } from '../components/ui/button';
import { CheckCircle, ArrowRight, ShoppingBag, Shield, FileCheck, Clock, Award, Users, Search } from 'lucide-react';
import TestimonialsSlider from '../components/TestimonialsSlider';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const AmazonBrandRegistryNew = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const pricingPlans = [
    { 
      name: 'Standard', 
      price: 99, 
      features: ['Amazon Brand Case Review', 'Amazon Brand Case Filing', '100% Satisfaction Guarantee', 'Email Support'] 
    },
    { 
      name: 'Diamond', 
      price: 149,
      popular: true,
      features: ['Amazon Brand Case Review', 'Amazon Brand Case Preparation', 'Amazon Brand Case Filing', '100% Satisfaction Guarantee', 'Refusal Risk Meter', 'Priority Processing', 'Dedicated Support'] 
    },
    { 
      name: 'Premium', 
      price: 199, 
      features: ['Amazon Brand Case Review', 'Amazon Brand Case Preparation', 'Amazon Brand Case Filing', '100% Satisfaction Guarantee', 'Refusal Risk Meter', 'Amazon Brand Case Monitoring', 'Complete Documentation', 'Digital File', 'Dedicated Case Manager', 'Same-Day Filing'] 
    }
  ];

  const faqs = [
    { q: 'What is Amazon Brand Registry?', a: 'Amazon Brand Registry is a program that helps protect your intellectual property on Amazon. It gives you enhanced control over your product listings, access to powerful search and reporting tools, and additional selling benefits like A+ Content and Sponsored Brands ads.' },
    { q: 'What do I need to enroll in Amazon Brand Registry?', a: 'You need an active registered trademark, either a text-based or image-based trademark with words, letters, or numbers. The trademark must be registered with a government trademark office in an eligible country.' },
    { q: 'How long does the enrollment process take?', a: 'Once your trademark is registered, the Amazon Brand Registry enrollment typically takes 1-3 business days. We handle the entire process and follow up with Amazon on your behalf.' },
    { q: 'Can I enroll with a pending trademark?', a: 'No, Amazon requires an active registered trademark. However, we can help you complete your trademark registration first, and then proceed with Brand Registry enrollment.' },
    { q: 'What are the benefits of Brand Registry?', a: 'Benefits include proactive brand protection, enhanced content tools, powerful search capabilities, predictive automation, and reporting on potential infringements. You also get access to Stores, A+ Content, and Sponsored Brands advertising.' }
  ];

  return (
    <div className="min-h-screen bg-navy-900">
      <Navbar onOpenModal={() => setIsModalOpen(true)} />
      
      {/* About the Service */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{backgroundImage: "url('https://images.unsplash.com/photo-1763630817183-28e58ebd0671?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMG1lZXRpbmclMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBtb2Rlcm4lMjBkYXJrfGVufDB8fHx8MTc3Mzk0Mjc0Nnww&ixlib=rb-4.1.0&q=85')"}}></div>
        <div className="absolute inset-0 banner-bg-navy"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center animate-fade-in">
            <h1 className="text-5xl font-bold text-gray-100 mb-6">Amazon Brand Registry Services</h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Protect your brand on Amazon's marketplace with professional Brand Registry enrollment. Get access to powerful tools, enhanced content features, and proactive brand protection starting at just $99.
            </p>
            <Button size="lg" onClick={() => setIsModalOpen(true)} className="bg-brand-gradient text-white hover:opacity-90 group animate-pulse-glow">
              Protect Your Amazon Brand
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </Button>
          </div>
        </div>
      </section>

      {/* 3-Step Process */}
      <section className="py-20 bg-navy-800">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-100 mb-12 animate-fade-in">Our Simple <span className="text-brand-primary">3-Step Process</span></h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { icon: FileCheck, title: 'Verify Your Trademark', desc: 'We verify that your trademark is registered and meets Amazon\'s eligibility requirements.' },
              { icon: Search, title: 'Prepare Enrollment', desc: 'Our team prepares all necessary documentation and information for your Brand Registry application.' },
              { icon: ShoppingBag, title: 'Complete Enrollment', desc: 'We submit your enrollment, follow up with Amazon, and ensure successful registration.' }
            ].map((step, idx) => {
              const Icon = step.icon;
              return (
                <div key={idx} className="text-center animate-scale-in bg-navy-800/50 rounded-xl p-6 hover:-translate-y-2 hover:bg-navy-700/80 hover:shadow-xl border border-transparent hover:border-gold-400/30 transition-all duration-300 cursor-default" style={{animationDelay: `${idx * 0.1}s`}}>
                  <div className="bg-brand-gradient w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse-glow">
                    <Icon className="text-white" size={32} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-100 mb-3">{step.title}</h3>
                  <p className="text-base text-gray-300 leading-relaxed">{step.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why You Need It */}
      <section className="py-20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-100 mb-12 animate-fade-in">Why You Need <span className="text-brand-primary">Amazon Brand Registry</span></h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              { title: 'Brand Protection', desc: 'Protect your intellectual property and prevent counterfeits on Amazon\'s marketplace.' },
              { title: 'Enhanced Content', desc: 'Access A+ Content, Stores, and enhanced product detail pages to showcase your brand.' },
              { title: 'Powerful Tools', desc: 'Get access to powerful search and reporting tools to find and report infringements.' },
              { title: 'Advertising Benefits', desc: 'Unlock Sponsored Brands ads and other advertising features exclusive to brand owners.' },
              { title: 'Predictive Protection', desc: 'Amazon\'s automated systems proactively detect and remove suspected infringing content.' },
              { title: 'Customer Trust', desc: 'Build customer trust with verified brand status and professional product listings.' }
            ].map((item, idx) => (
              <div key={idx} className="bg-navy-700 rounded-xl p-6 shadow-lg hover:shadow-2xl hover:-translate-y-2 hover:border-gold-400/50 border border-transparent transition-all duration-300 cursor-default animate-scale-in" style={{animationDelay: `${idx * 0.1}s`}}>
                <h3 className="text-xl font-bold text-gray-100 mb-3">{item.title}</h3>
                <p className="text-base text-gray-300">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-navy-800">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 animate-fade-in">
            <h2 className="text-4xl font-bold text-gray-100 mb-4">Affordable Amazon Brand Registry Packages</h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">Choose the package that fits your brand protection needs</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {pricingPlans.map((plan, idx) => (
              <div key={plan.name} className={`rounded-2xl p-8 transition-all duration-300 hover:scale-105 flex flex-col h-full animate-scale-in ${plan.popular ? 'bg-gradient-to-br from-gold-400 to-gold-500 text-white shadow-2xl ring-4 ring-gold-400/30' : 'bg-navy-700 shadow-lg hover:shadow-xl border border-navy-500/30'}`} style={{animationDelay: `${idx * 0.1}s`}}>
                <div className="text-center mb-6">
                  <h3 className={`text-2xl font-bold mb-2 ${plan.popular ? 'text-white' : 'text-gray-100'}`}>{plan.name}</h3>
                  <div className="flex items-baseline justify-center">
                    <span className={`text-5xl font-bold ${plan.popular ? 'text-white' : 'text-brand-primary'}`}>${plan.price}</span>
                  </div>
                  <p className={`text-sm mt-2 ${plan.popular ? 'text-gray-200' : 'text-gray-400'}`}>One-time fee</p>
                </div>
                <div className={`flex-grow mb-6 overflow-y-auto max-h-64 pr-2 ${plan.popular ? 'scrollbar-thin-blue' : 'scrollbar-thin'}`}>
                  <div className="space-y-3">
                    {plan.features.map((feature, i) => (
                      <div key={i} className="flex items-start space-x-2">
                        <CheckCircle className={`flex-shrink-0 mt-0.5 ${plan.popular ? 'text-gold-200' : 'text-brand-primary'}`} size={20} />
                        <span className={`text-sm ${plan.popular ? 'text-white' : 'text-gray-300'}`}>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <Button onClick={() => setIsModalOpen(true)} className={`w-full mt-auto ${plan.popular ? 'bg-navy-500 text-white hover:bg-navy-400 border-2 border-navy-400/60' : 'bg-brand-gradient text-white hover:opacity-90'}`}>Choose Plan</Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-100 mb-16 animate-fade-in">Why Choose <span className="text-brand-primary">Trademark Nexor</span></h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {[
              { icon: Shield, title: 'Amazon Experts', desc: 'Specialized team with extensive Amazon Brand Registry experience' },
              { icon: Clock, title: 'Fast Enrollment', desc: 'Quick turnaround with most enrollments completed within days' },
              { icon: Award, title: '99% Success Rate', desc: 'High success rate with expert application preparation' },
              { icon: Users, title: 'Ongoing Support', desc: 'Continued support even after successful enrollment' }
            ].map((item, idx) => {
              const Icon = item.icon;
              return (
                <div key={idx} className="text-center animate-scale-in bg-navy-800/50 rounded-xl p-6 hover:-translate-y-2 hover:bg-navy-700/80 hover:shadow-xl border border-transparent hover:border-gold-400/30 transition-all duration-300 cursor-default" style={{animationDelay: `${idx * 0.1}s`}}>
                  <div className="bg-navy-700 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Icon className="text-brand-primary" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-100 mb-2">{item.title}</h3>
                  <p className="text-base text-gray-300">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-navy-800">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="animate-fade-in">
              <img src="https://images.unsplash.com/photo-1661956602944-249bcd04b63f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHw0fHxlY29tbWVyY2UlMjBvbmxpbmUlMjBtYXJrZXRwbGFjZSUyMHByb2R1Y3QlMjBicmFuZGluZ3xlbnwwfHx8fDE3NzM5NDAxMjJ8MA&ixlib=rb-4.1.0&q=85&w=800" alt="Amazon Brand Registry Benefits" className="rounded-2xl shadow-xl hover-zoom" />
            </div>
            <div className="animate-slide-up">
              <h2 className="text-4xl font-bold text-gray-100 mb-6">Key Benefits of Our Service</h2>
              <div className="space-y-4">
                {[
                  'Trademark eligibility verification and guidance',
                  'Complete enrollment application preparation',
                  'Direct communication with Amazon on your behalf',
                  'Fast processing with priority support options',
                  'Post-enrollment guidance and support',
                  '100% satisfaction guarantee on all services'
                ].map((benefit, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <CheckCircle className="text-brand-primary flex-shrink-0 mt-1" size={24} />
                    <p className="text-lg text-gray-200">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <TestimonialsSlider />

      {/* FAQs */}
      <section className="py-20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-center text-gray-100 mb-12 animate-fade-in">Frequently Asked <span className="text-brand-primary">Questions</span></h2>
            <div className="bg-navy-700 rounded-2xl shadow-xl p-8">
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, idx) => (
                  <AccordionItem key={idx} value={`item-${idx}`}>
                    <AccordionTrigger className="text-left font-semibold text-gray-100">{faq.q}</AccordionTrigger>
                    <AccordionContent className="text-base text-gray-300">{faq.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-brand-gradient">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center animate-fade-in">
            <h2 className="text-4xl font-bold text-white mb-6">Ready to Protect Your Amazon Brand?</h2>
            <p className="text-xl text-gray-200 mb-8">Enroll in Amazon Brand Registry today and unlock powerful brand protection tools</p>
            <Button size="lg" onClick={() => setIsModalOpen(true)} className="bg-navy-500 text-white hover:bg-navy-400 shadow-xl border-2 border-navy-400">
              Get Started Now
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default AmazonBrandRegistryNew;